import React from "react";
import clsx from "clsx";
import { CopyToCliboard } from "@components/CustomTable/helpers";
import { FIRST_INDEX, FIRST_ELEMENT, TOOLTIP_TIMEOUT, TEXT_COLORS, SOURCE_COLORS } from "./constants";

/**
 * Creates the Mismatches column definition - shared between OutstandingRecords and ExceptionDetails
 */
export const createMismatchesColumn = (translate) => ({
  accessorKey: "Mismatches",
  header: translate("outstanding-records.columns.Mismatches") || translate("exception-details.columns.Mismatches"),
  size: 110,
  sortable: true,
  canPin: false,
  alignment: "left",
  canFilter: false,
  cell: ({ row }) => {
    return (
      <div className="flex flex-col gap-1">
        {row.original.Mismatches.map((mismatch, index) => (
          <span
            key={index}
            className={`rounded-full items-center justify-between gap-2 w-fit font-normal text-sm border border-r-10 border-transparent `}
          >
            <span className={`${TEXT_COLORS[mismatch.MismatchSeverityTag]}`}>
              {mismatch.MismatchName}
            </span>
          </span>
        ))}
      </div>
    );
  },
});

/**
 * Creates the MatchingSource column definition - shared between OutstandingRecords and ExceptionDetails
 */
export const createMatchingSourceColumn = (translate) => ({
  accessorKey: "MatchingSource",
  header: translate("outstanding-records.columns.MatchingSource") || translate("exception-details.columns.MatchingSource"),
  size: 110,
  sortable: false,
  alignment: "left",
  canPin: false,
  canFilter: false,
  cell: ({ row }) => {
    return (
      !!row.original.MatchingSource.MatchingSourceName &&
      row.original.MatchingSource.MatchingSourceName +
        ` (${translate("outstanding-records.columns.RunID")}: ${
          row.original.RunId
        })`
    );
  },
});

/**
 * Creates the BusinessDay column definition - shared between OutstandingRecords and ExceptionDetails
 */
export const createBusinessDayColumn = (translate) => ({
  accessorKey: "BusinessDay",
  header: translate("outstanding-records.columns.BusinessDay") || translate("exception-details.columns.BusinessDay"),
  size: 60,
  sortable: true,
  canPin: false,
  alignment: "left",
  canFilter: false,
  filterType: "date",
  cell: ({ row }) => {
    return (
      <span className="text-sm flex flex-col font-normal">
        <span>{row.original.BusinessDay}</span>
        <span>{row.original.BusinessDayInstance}</span>
      </span>
    );
  },
});

/**
 * Creates the CreationDate column definition - shared between OutstandingRecords and ExceptionDetails
 */
export const createCreationDateColumn = (translate) => ({
  accessorKey: "CreationDate",
  header: translate("outstanding-records.columns.CreationDate") || translate("exception-details.columns.CreationDate"),
  size: 80,
  sortable: true,
  canFilter: false,
  canPin: false,
  filterType: "date",
  splitCellValue: "T",
});

/**
 * Creates the RecordMatchingKeys column definition - shared between OutstandingRecords and ExceptionDetails
 */
export const createRecordMatchingKeysColumn = (translate) => ({
  accessorKey: "RecordMatchingKeys",
  header: translate("outstanding-records.columns.RecordMatchingKeys") || translate("exception-details.columns.RecordMatchingKeys"),
  size: 70,
  sortable: true,
  canPin: false,
  cell: ({ row }) => {
    const values = row.original.RecordMatchingKeys;
    return (
      <div className="flex flex-col gap-1">
        {values.map((value, index) => {
          return <span key={index}>{value}</span>;
        })}
      </div>
    );
  },
});

/**
 * Creates shared children columns including source, reconciliation stream, matching source, and location info
 */
export const createSharedChildrenColumns = (translate, setCurrentTooltip) => [
  {
    accessorKey: "source",
    header: translate("outstanding-records.children-columns.source") || translate("exception-details.children-columns.source"),
    hideHeader: true,
    size: 20,
    alignment: "left",
    cell: ({ row }) => {
      return (
        <span
          className={clsx(
            `text-xs font-semibold flex gap-2 items-center`,
            SOURCE_COLORS[row.original.source] || "text-gray-400"
          )}
          title={row.original.source}
        >
          {row.original.source.split("")[FIRST_INDEX].toUpperCase()}
        </span>
      );
    },
  },
  {
    accessorKey: "ReconciliationStreamName",
    header: translate(
      "outstanding-records.children-columns.ReconciliationStreamName"
    ) || translate("exception-details.children-columns.ReconciliationStreamName"),
    size: 210,
    alignment: "left",
  },
  {
    accessorKey: "MatchingSource",
    header: translate("outstanding-records.children-columns.DataSourceName") || translate("exception-details.children-columns.DataSourceName"),
    alignment: "left",
    size: 150,
    cell: ({ row }) => {
      return (
        !!row.original.MatchingSource.MatchingSourceName &&
        row.original.MatchingSource.MatchingSourceName +
          ` (${translate("outstanding-records.columns.RunID")}: ${
            row.original.RunId
          })`
      );
    },
  },
  {
    accessorKey: "AreaName",
    header: translate("outstanding-records.children-columns.AreaName") || translate("exception-details.children-columns.AreaName"),
    alignment: "left",
    size: 120,
  },
  {
    accessorKey: "OutletName",
    header: translate("outstanding-records.children-columns.OutletName") || translate("exception-details.children-columns.OutletName"),
    alignment: "left",
    size: 130,
  },
  {
    accessorKey: "TransactionTerminalCode",
    header: translate(
      "outstanding-records.children-columns.TransactionTerminalCode"
    ) || translate("exception-details.children-columns.TransactionTerminalCode"),
    size: 120,
  },
  {
    accessorKey: "RecordReference",
    header: translate("outstanding-records.children-columns.RecordReference") || translate("exception-details.children-columns.RecordReference"),
    size: 120,
  },
  {
    accessorKey: "RecordDate",
    header: translate("outstanding-records.children-columns.RecordDate") || translate("exception-details.children-columns.RecordDate"),
    size: 120,
    splitCellValue: "T",
  },
  {
    accessorKey: "RecordType",
    header: translate("outstanding-records.children-columns.RecordType") || translate("exception-details.children-columns.RecordType"),
    size: 100,
  },
  {
    accessorKey: "Attributes",
    header: translate("outstanding-records.children-columns.Attributes") || translate("exception-details.children-columns.Attributes"),
    size: 100,
    displayInPopup: true,
    popupCellDisplay: (value) => {
      return (
        <div
          data-tooltip-id="attributes-tooltip"
          onMouseOver={() => {
            setCurrentTooltip(value);
          }}
          onMouseOut={() =>
            setTimeout(() => {
              setCurrentTooltip(null);
            }, TOOLTIP_TIMEOUT)
          }
          className="relative text-primary-400 font-semibold"
        >
          {"("}
          {value.length}
          {")"}
        </div>
      );
    },
    popupContent: (row) => {
      const value = row.Attributes;
      return (
        <div className="relative ">
          <div className="flex flex-col dark:text-white min-w-[400px] transition-all bg-primary-dark text-[#777f9e] text-14 rounded-lg px-[2px] py-[8px] mb-3 opacity-100">
            {value &&
              value.map((attr, index) => (
                <div
                  key={index}
                  className="flex flex-row justify-between gap-2 mb-[2px]"
                >
                  <span className="py-[2px] whitespace-nowrap text-xs font-semibold">
                    {attr.Key}
                  </span>
                  <span className="relative py-[2px] flex gap-2 items-center px-[8px] whitespace-nowrap ltr:text-right rtl:text-left text-sm">
                    {attr.Value}
                    <div className=" absolute left-0 w-full justify-center flex opacity-0 hover:opacity-100 dark:bg-opacity-65 dark:bg-gray-700 bg-gray-300 bg-opacity-50 rounded">
                      <CopyToCliboard size="5" value={attr.Value} />
                    </div>
                  </span>
                </div>
              ))}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "PaymentMethod",
    header: translate("outstanding-records.children-columns.PaymentMethod") || translate("exception-details.children-columns.PaymentMethod"),
    alignment: "left",
    size: 100,
  },
  {
    accessorKey: "Amount",
    header: translate("outstanding-records.children-columns.Amount") || translate("exception-details.children-columns.Amount"),
    size: 100,
    cell: ({ row }) => {
      return (
        <span className="flex items-center justify-center group text-primary-400 font-semibold">
          {`${row.original.Currency} ${
            row.original.SourceAmount || row.original.DestinationAmount
          }`}
        </span>
      );
    },
  },
];
